import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCustomerContext } from '@unlikelystudio/react-ecommerce-hooks'

import useAlerts from "~/hooks/useAlerts"
import type { UpdateWishlistInput, WishlistUpdateResponse } from "~/hooks/wishlist/useAddProductToWishlist"
import { useWishlistQueryKey, type WishlistResponse } from "~/hooks/wishlist/useGetWishlist"
import { useWishlistCookie } from "~/hooks/wishlist/useWishlistCookie"

import { updateWishlistProducts } from '~/lib/handle-api'

import type { UpdatedProductRequestPayload, UpdateWishlistProductsRequestBody } from "~/lib/swym/wishlist/types"
import { addGIDPrefix } from '~/utils/shopify-ids'



export function useRemoveProductToWishlist({ onSuccess }: { onSuccess?: () => void }) {
  const { wishlistCookie } = useWishlistCookie()
  const wishlistQueryKey = useWishlistQueryKey()
  const queryClient = useQueryClient()
  const { customerAccessToken } = useCustomerContext()

  const triggerSuccessAlert = useAlerts()
  const triggerErrorAlert = useAlerts()


  return useMutation<WishlistUpdateResponse, unknown, UpdateWishlistInput, { prevResponse: WishlistResponse }>(
    async (params) => {
      const requestPayload: UpdateWishlistProductsRequestBody = {
        wishlist: wishlistCookie,
        action: "delete",
        products: [
          {
            epi: params.variantId,
            empi: params.productId,
            du: `${process.env.NEXT_PUBLIC_VERCEL_URL}${params.link.href}`,
            quantity: params.quantity ?? 1,
            uuid: params.uuid,
          },
        ],
      }

      const response: UpdatedProductRequestPayload = await updateWishlistProducts({
        accessToken: customerAccessToken,
        payload: requestPayload,
      })

      return {
        products: response?.d,
        ...response,
      }
    },
    {
      // Optimistically update the wishlist
      onMutate: async ({ variantId }) => {
        await queryClient.cancelQueries(wishlistQueryKey)

        const prevResponse = queryClient.getQueryData<WishlistResponse>(wishlistQueryKey)

        if (prevResponse) {
          queryClient.setQueryData(wishlistQueryKey, {
            ...prevResponse,
            products: prevResponse?.products?.filter((product) => product?.variantId !== addGIDPrefix("ProductVariant", variantId)),
          })
        }

        return { prevResponse }
      },
      onSuccess: (data) => {
        if (data?.products?.[0]?.hasError || data?.success === false) {
          triggerErrorAlert()
          return
        }

        onSuccess?.()
        triggerSuccessAlert("REMOVED_WISHLIST")

      },
      onError: (_err, _variables, context) => {
        // If the mutation fails, use the previous value as a backup
        if (context?.prevResponse) {
          queryClient.setQueryData(wishlistQueryKey, context.prevResponse)
        }
        triggerErrorAlert()
      },
      onSettled: () => {
        // Invalidate and refetch the wishlist
        queryClient.invalidateQueries([wishlistQueryKey[0]], { exact: false })
      },
    }
  )
}


